import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatIDR } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Printer, MessageCircle, Search, Receipt, X } from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES, flagFor } from "../lib/countries";

const STATUSES = ["Draft", "Sent", "Paid", "Cancelled"];
const STATUS_COLORS = {
  Draft: "bg-stone-100 text-stone-700",
  Sent: "bg-blue-100 text-blue-700",
  Paid: "bg-emerald-100 text-emerald-700",
  Cancelled: "bg-rose-100 text-rose-700",
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyItem = () => ({ description: "", quantity: 1, unit_price: 0 });

const emptyForm = {
  invoice_number: "",
  invoice_date: todayISO(),
  guest_name: "",
  phone_number: "",
  country: "",
  language: "id",
  items: [emptyItem()],
  discount: 0,
  deposit: 0,
  status: "Draft",
  notes: "",
};

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("All");
  const [knownGuests, setKnownGuests] = useState([]);  // from /guests for autocomplete

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showGuestSuggest, setShowGuestSuggest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, g] = await Promise.all([
        api.get("/custom-invoices"),
        api.get("/guests").catch(() => ({ data: [] })),
      ]);
      setRows(r.data || []);
      setKnownGuests(g.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = [...rows];
    if (fStatus !== "All") list = list.filter((r) => r.status === fStatus);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          (r.guest_name || "").toLowerCase().includes(q) ||
          (r.invoice_number || "").toLowerCase().includes(q) ||
          (r.phone_number || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [rows, search, fStatus]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, items: [emptyItem()] });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      invoice_number: row.invoice_number || "",
      invoice_date: row.invoice_date || todayISO(),
      guest_name: row.guest_name || "",
      phone_number: row.phone_number || "",
      country: row.country || "",
      language: row.language || "id",
      items: row.items?.length ? [...row.items] : [emptyItem()],
      discount: row.discount || 0,
      deposit: row.deposit || 0,
      status: row.status || "Draft",
      notes: row.notes || "",
    });
    setDialogOpen(true);
  };

  const calcSubtotal = (items) =>
    items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);

  const submit = async () => {
    if (!form.guest_name.trim()) return toast.error("Nama tamu wajib diisi");
    if (form.items.length === 0 || form.items.every((i) => !i.description.trim())) {
      return toast.error("Minimal 1 item dengan deskripsi");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        invoice_number: form.invoice_number?.trim() || null,
        items: form.items
          .filter((i) => i.description.trim())
          .map((i) => ({
            description: i.description,
            quantity: Number(i.quantity) || 1,
            unit_price: Number(i.unit_price) || 0,
          })),
        discount: Number(form.discount) || 0,
        deposit: Number(form.deposit) || 0,
      };
      if (editing) {
        await api.put(`/custom-invoices/${editing.id}`, payload);
        toast.success("Invoice diperbarui");
      } else {
        await api.post("/custom-invoices", payload);
        toast.success("Invoice dibuat");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/custom-invoices/${deleteId}`);
      toast.success("Invoice dihapus");
      setDeleteId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    }
  };

  const sendWa = (r) => {
    if (!r.phone_number) return toast.error("Tamu belum punya nomor WA");
    const subtotal = (r.items || []).reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
    const total = subtotal - (r.discount || 0);
    const outstanding = Math.max(0, total - (r.deposit || 0));
    const isEN = r.language === "en";
    const lines = [
      isEN ? `Halo ${r.guest_name}, here's your invoice:` : `Halo ${r.guest_name}, berikut invoice Anda:`,
      "",
      `*${r.invoice_number}* (${r.invoice_date})`,
      "",
      ...(r.items || []).map(
        (i) =>
          `• ${i.description} — ${i.quantity} × ${formatIDR(i.unit_price)} = *${formatIDR((i.quantity || 0) * (i.unit_price || 0))}*`
      ),
      "",
      `${isEN ? "Subtotal" : "Subtotal"}: ${formatIDR(subtotal)}`,
      ...(r.discount > 0 ? [`${isEN ? "Discount" : "Diskon"}: -${formatIDR(r.discount)}`] : []),
      `*${isEN ? "Total" : "Total"}: ${formatIDR(total)}*`,
      ...(r.deposit > 0 ? [`${isEN ? "Deposit paid" : "DP diterima"}: ${formatIDR(r.deposit)}`] : []),
      ...(outstanding > 0 ? [`*${isEN ? "Balance due" : "Sisa pembayaran"}: ${formatIDR(outstanding)}*`] : []),
    ];
    const text = lines.join("\n");
    const phone = r.phone_number.replace(/\D/g, "").replace(/^0/, "62");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <>
      <PageHeader
        title="Invoice"
        subtitle="Buat & kirim invoice standalone untuk trip, transfer, atau service lain."
        actions={
          <Button data-testid="btn-new-invoice" onClick={openNew} className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl">
            <Plus className="h-4 w-4 mr-2" /> Invoice Baru
          </Button>
        }
      />

      {/* Filters */}
      <div className="bg-white border border-[#EBE6DE] rounded-2xl p-4 shadow-sm flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#586D61]" />
          <Input data-testid="input-search-invoice" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama / no. invoice / HP…" className="pl-9 border-[#EBE6DE]" />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="filter-invoice-status" className="w-[160px] bg-white"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">Semua Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#EBE6DE] rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FDFBF7]">
                <TableHead className="font-bold text-[#172E21]">No. Invoice</TableHead>
                <TableHead className="font-bold text-[#172E21]">Tanggal</TableHead>
                <TableHead className="font-bold text-[#172E21]">Tamu</TableHead>
                <TableHead className="font-bold text-[#172E21] text-center">Items</TableHead>
                <TableHead className="font-bold text-[#172E21] text-right">Total</TableHead>
                <TableHead className="font-bold text-[#172E21] text-right">Sisa</TableHead>
                <TableHead className="font-bold text-[#172E21]">Status</TableHead>
                <TableHead className="font-bold text-[#172E21] text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="text-center py-10 text-[#586D61]">Memuat…</TableCell></TableRow>}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-[#586D61]">
                  <Receipt className="h-12 w-12 mx-auto text-[#586D61]/30 mb-2" />
                  Belum ada invoice. Klik "Invoice Baru".
                </TableCell></TableRow>
              )}
              {filtered.map((r) => {
                const subtotal = (r.items || []).reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
                const total = subtotal - (r.discount || 0);
                const dep = r.status === "Paid" ? total : (r.deposit || 0);
                const outstanding = Math.max(0, total - dep);
                return (
                  <TableRow key={r.id} data-testid={`invoice-row-${r.id}`} className="hover:bg-[#FDFBF7]">
                    <TableCell className="font-mono text-sm font-medium text-[#172E21]">{r.invoice_number}</TableCell>
                    <TableCell className="text-sm font-mono">{r.invoice_date}</TableCell>
                    <TableCell>
                      <div className="font-medium text-[#172E21] flex items-center gap-1.5">
                        {r.country && <span className="leading-none">{flagFor(r.country)}</span>}
                        {r.guest_name}
                      </div>
                      {r.phone_number && <div className="text-[10px] text-[#586D61] font-mono">{r.phone_number}</div>}
                    </TableCell>
                    <TableCell className="text-center font-mono text-sm">{(r.items || []).length}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-[#D16B4D]">{formatIDR(total)}</TableCell>
                    <TableCell className={`text-right font-mono ${outstanding > 0 ? "text-[#D99338] font-bold" : "text-[#586D61]"}`}>
                      {outstanding > 0 ? formatIDR(outstanding) : "Lunas"}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_COLORS[r.status] || "bg-stone-100 text-stone-700"}`}>
                        {r.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Link to={`/invoices/${r.id}/print`} target="_blank" rel="noopener noreferrer" data-testid={`btn-print-invoice-${r.id}`}>
                          <Button variant="ghost" size="icon" className="text-[#586D61] hover:text-[#082618]" title="Print">
                            <Printer className="h-4 w-4" />
                          </Button>
                        </Link>
                        {r.phone_number && (
                          <Button variant="ghost" size="icon" onClick={() => sendWa(r)} className="text-[#25D366] hover:text-[#1FA851]" title="Kirim WA" data-testid={`btn-wa-invoice-${r.id}`}>
                            <MessageCircle className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => openEdit(r)} className="text-[#586D61] hover:text-[#082618]" title="Edit" data-testid={`btn-edit-invoice-${r.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteId(r.id)} className="text-[#D14D4D] hover:text-[#B83E3E]" title="Hapus" data-testid={`btn-delete-invoice-${r.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl bg-white" data-testid="invoice-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit Invoice" : "Invoice Baru"}</DialogTitle>
            <DialogDescription className="text-[#586D61]">
              Untuk trip, transfer boat, atau service add-on. Nomor invoice otomatis kalau dikosongkan.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm">Tanggal</Label>
              <Input data-testid="input-invoice-date" type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-sm">No. Invoice <span className="text-[#586D61] font-normal">(opsional)</span></Label>
              <Input data-testid="input-invoice-number" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="Auto" className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-sm">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="select-invoice-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2 relative">
              <Label className="text-sm">Nama Tamu</Label>
              <Input
                data-testid="input-invoice-guest"
                value={form.guest_name}
                onChange={(e) => {
                  setForm({ ...form, guest_name: e.target.value });
                  setShowGuestSuggest(true);
                }}
                onFocus={() => setShowGuestSuggest(true)}
                onBlur={() => setTimeout(() => setShowGuestSuggest(false), 200)}
                className="mt-1.5"
                placeholder="Ketik untuk cari dari database…"
                autoComplete="off"
              />
              {showGuestSuggest && form.guest_name.trim().length >= 1 && (() => {
                const q = form.guest_name.toLowerCase();
                const matches = knownGuests
                  .filter((g) => (g.guest_name || "").toLowerCase().includes(q) && (g.guest_name || "").toLowerCase() !== q)
                  .slice(0, 6);
                if (matches.length === 0) return null;
                return (
                  <div
                    data-testid="guest-suggestions"
                    className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-[#EBE6DE] rounded-xl shadow-lg max-h-56 overflow-y-auto"
                  >
                    {matches.map((g) => (
                      <button
                        key={g.key}
                        type="button"
                        data-testid={`guest-suggest-${g.key}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setForm({
                            ...form,
                            guest_name: g.guest_name || "",
                            phone_number: g.phone_number || form.phone_number,
                            country: g.country || form.country,
                            language: g.country && g.country !== "Indonesia" ? "en" : (form.language || "id"),
                          });
                          setShowGuestSuggest(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-[#FDFBF7] border-b border-[#EBE6DE]/50 last:border-0"
                      >
                        <div className="font-medium text-sm text-[#172E21] flex items-center gap-1.5">
                          {g.country && <span className="leading-none">{flagFor(g.country)}</span>}
                          {g.guest_name}
                        </div>
                        <div className="text-[10px] text-[#586D61] font-mono">
                          {g.phone_number || "Tidak ada HP"} {g.country ? `· ${g.country}` : ""} · {g.total_bookings} stay
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div>
              <Label className="text-sm">Bahasa</Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="id">Bahasa Indonesia</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm">No. WhatsApp</Label>
              <Input data-testid="input-invoice-phone" value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="0812..." className="mt-1.5 font-mono" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm">Negara</Label>
              <Select value={form.country || "__none__"} onValueChange={(v) => setForm({ ...form, country: v === "__none__" ? "" : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pilih negara" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">— Tidak disebut —</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.name}>{c.flag} {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-bold">Item / Service</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })} data-testid="btn-add-item">
                <Plus className="h-3 w-3 mr-1" /> Tambah Item
              </Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6">
                    <Input
                      data-testid={`input-item-desc-${idx}`}
                      value={it.description}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...it, description: e.target.value };
                        setForm({ ...form, items });
                      }}
                      placeholder="mis: Diving Pianemo, Transfer boat Sorong"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      data-testid={`input-item-qty-${idx}`}
                      type="number"
                      min={0}
                      step="0.5"
                      value={it.quantity}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...it, quantity: e.target.value };
                        setForm({ ...form, items });
                      }}
                      placeholder="Qty"
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      data-testid={`input-item-price-${idx}`}
                      type="number"
                      min={0}
                      value={it.unit_price}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...it, unit_price: e.target.value };
                        setForm({ ...form, items });
                      }}
                      placeholder="Harga"
                      className="font-mono"
                    />
                  </div>
                  <div className="col-span-1">
                    <Button type="button" variant="ghost" size="icon" onClick={() => {
                      const items = form.items.filter((_, i) => i !== idx);
                      setForm({ ...form, items: items.length ? items : [emptyItem()] });
                    }} className="text-[#D14D4D]"><X className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-right text-sm space-y-0.5">
              <div className="text-[#586D61]">Subtotal: <span className="font-mono font-medium text-[#172E21]">{formatIDR(calcSubtotal(form.items))}</span></div>
              {form.discount > 0 && <div className="text-[#586D61]">Diskon: <span className="font-mono">-{formatIDR(Number(form.discount) || 0)}</span></div>}
              <div className="text-[#D16B4D] font-bold text-base">Total: <span className="font-mono">{formatIDR(calcSubtotal(form.items) - (Number(form.discount) || 0))}</span></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Diskon (Rp)</Label>
              <Input data-testid="input-invoice-discount" type="number" min={0} value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-sm">DP Diterima (Rp)</Label>
              <Input data-testid="input-invoice-deposit" type="number" min={0} value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} className="mt-1.5 font-mono" />
            </div>
          </div>

          <div>
            <Label className="text-sm">Catatan (opsional)</Label>
            <Textarea data-testid="input-invoice-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Termasuk peralatan, akomodasi, dll." className="mt-1.5 resize-none" rows={2} />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Batal</Button>
            <Button data-testid="btn-save-invoice" onClick={submit} disabled={saving} className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl">
              {saving ? "Menyimpan…" : (editing ? "Simpan Perubahan" : "Buat Invoice")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">Hapus Invoice?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#586D61]">Tindakan ini tidak bisa dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-confirm-delete-invoice" onClick={doDelete} className="bg-[#D14D4D] hover:bg-[#B83E3E] text-white rounded-xl">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
